/**
 * Allocation-free state copy for rollback (ARCHITECTURE §4). Hand-written for speed: every field is copied
 * explicitly. When you add a field to SimState you MUST copy it here — `clone.test.ts` mutates every leaf of a
 * state and fails if the copy misses anything.
 */
import type {
  BallSim,
  CameraHint,
  InputHistory,
  PlayResult,
  PlayerSim,
  RulesSim,
  SimConfig,
  SimState,
  SpecialSim,
} from './state';
import { createBlankState } from './state';

const copyArr = (dst: number[], src: readonly number[]): void => {
  for (let i = 0; i < src.length; i++) dst[i] = src[i]!;
};

const copyInput = (d: InputHistory, s: InputHistory): void => {
  d.cur = s.cur;
  d.prev = s.prev;
  copyArr(d.lastPress, s.lastPress);
  copyArr(d.prevPress, s.prevPress);
  copyArr(d.held, s.held);
  copyArr(d.buffer, s.buffer);
};

const copySpecial = (d: SpecialSim, s: SpecialSim): void => {
  d.id = s.id;
  d.phase = s.phase;
  d.frame = s.frame;
  d.timer = s.timer;
  d.decoyActive = s.decoyActive;
  d.decoyX = s.decoyX;
  d.decoyY = s.decoyY;
  d.decoyVx = s.decoyVx;
  d.decoyVy = s.decoyVy;
  d.decoyFacing = s.decoyFacing;
  d.decoyFrames = s.decoyFrames;
  d.cooldownFrames = s.cooldownFrames;
  d.usesThisDrive = s.usesThisDrive;
};

export const copyPlayer = (d: PlayerSim, s: PlayerSim): void => {
  d.index = s.index;
  d.character = s.character;
  d.stats.speedMul = s.stats.speedMul;
  d.stats.jumpMul = s.stats.jumpMul;
  d.stats.armMul = s.stats.armMul;
  d.stats.tackleMul = s.stats.tackleMul;
  d.stats.tossMul = s.stats.tossMul;
  d.stats.headRadius = s.stats.headRadius;
  d.x = s.x;
  d.y = s.y;
  d.vx = s.vx;
  d.vy = s.vy;
  d.facing = s.facing;
  d.grounded = s.grounded;
  d.state = s.state;
  d.stateFrame = s.stateFrame;
  d.prevState = s.prevState;
  d.airFrame = s.airFrame;
  d.hurdling = s.hurdling;
  d.hopPhase = s.hopPhase;
  d.dashDir = s.dashDir;
  d.stillFrames = s.stillFrames;
  d.burstFrames = s.burstFrames;
  d.throwCharge = s.throwCharge;
  d.lob = s.lob;
  d.truckCharge = s.truckCharge;
  d.stunFrames = s.stunFrames;
  d.iFrames = s.iFrames;
  d.beatenFrames = s.beatenFrames;
  d.beatenTick = s.beatenTick;
  d.spinDodged = s.spinDodged;
  d.glovePose = s.glovePose;
  d.gloveX = s.gloveX;
  d.gloveY = s.gloveY;
  d.gloveTargetX = s.gloveTargetX;
  d.gloveTargetY = s.gloveTargetY;
  d.stamina = s.stamina;
  d.power = s.power;
  copySpecial(d.special, s.special);
  copyInput(d.input, s.input);
  d.pumpFakesUsed = s.pumpFakesUsed;
  d.hardCountsUsed = s.hardCountsUsed;
  d.crossedLos = s.crossedLos;
  d.feintTick = s.feintTick;
  d.feintKind = s.feintKind;
  d.commitTick = s.commitTick;
  d.commitKind = s.commitKind;
  d.playBestX = s.playBestX;
  d.flags = s.flags;
  d.statYards = s.statYards;
  d.statPassYards = s.statPassYards;
  d.statTackles = s.statTackles;
  d.statCatches = s.statCatches;
  d.statInterceptions = s.statInterceptions;
  d.statSacks = s.statSacks;
  d.statBaits = s.statBaits;
  d.statTouchdowns = s.statTouchdowns;
};

const copyBall = (d: BallSim, s: BallSim): void => {
  d.state = s.state;
  d.holder = s.holder;
  d.x = s.x;
  d.y = s.y;
  d.vx = s.vx;
  d.vy = s.vy;
  d.angle = s.angle;
  d.angVel = s.angVel;
  d.thrownBy = s.thrownBy;
  d.airFrames = s.airFrames;
  d.releaseX = s.releaseX;
  d.forwardPass = s.forwardPass;
  d.passedLos = s.passedLos;
  d.fgAttempt = s.fgAttempt;
  d.hailMary = s.hailMary;
  d.touchedMask = s.touchedMask;
  d.lastTouchedBy = s.lastTouchedBy;
  d.bounces = s.bounces;
  d.kick = s.kick;
  d.looseFrames = s.looseFrames;
};

const copyResult = (d: PlayResult, s: PlayResult): void => {
  d.kind = s.kind;
  d.actor = s.actor;
  d.yards = s.yards;
  d.points = s.points;
  d.scorer = s.scorer;
  d.spotX = s.spotX;
  d.firstDown = s.firstDown;
};

const copyRules = (d: RulesSim, s: RulesSim): void => {
  d.phase = s.phase;
  d.phaseFrame = s.phaseFrame;
  d.format = s.format;
  d.offense = s.offense;
  d.attackDir = s.attackDir;
  copyArr(d.drivesStarted, s.drivesStarted);
  d.drivesPerPlayer = s.drivesPerPlayer;
  d.driveStartX = s.driveStartX;
  d.down = s.down;
  d.distance = s.distance;
  d.los = s.los;
  d.firstDownX = s.firstDownX;
  d.goalToGo = s.goalToGo;
  d.playClock = s.playClock;
  d.rushCount = s.rushCount;
  d.rushSkipped = s.rushSkipped;
  d.playCall = s.playCall;
  d.windX = s.windX;
  d.offenseCrossed = s.offenseCrossed;
  d.passThrown = s.passThrown;
  d.snapTick = s.snapTick;
  d.playCount = s.playCount;
  copyArr(d.score, s.score);
  d.conversion = s.conversion;
  d.conversionPlay = s.conversionPlay;
  d.overtime = s.overtime;
  d.otRound = s.otRound;
  d.afterDead = s.afterDead;
  copyResult(d.lastResult, s.lastResult);
  d.winner = s.winner;
  d.gameClock = s.gameClock;
  d.half = s.half;
  d.clockRunning = s.clockRunning;
  d.prevBallState = s.prevBallState;
  d.prevTouchedMask = s.prevTouchedMask;
  copyArr(d.baitsCounted, s.baitsCounted);
  copyArr(d.yardPower, s.yardPower);
  d.completion = s.completion;
  d.catchX = s.catchX;
};

const copyCamera = (d: CameraHint, s: CameraHint): void => {
  d.x = s.x;
  d.y = s.y;
  d.zoom = s.zoom;
  d.focus = s.focus;
};

const copyConfig = (d: SimConfig, s: SimConfig): void => {
  d.seed = s.seed;
  d.format = s.format;
  d.normalizedStats = s.normalizedStats;
  d.specials = s.specials;
  d.drivesPerPlayer = s.drivesPerPlayer;
  d.firstPossession = s.firstPossession;
  copyArr(d.characters, s.characters);
  d.wind = s.wind;
};

/** Copy `src` into `dst` in place. No allocation. Both must come from createBlankState/createInitialState/cloneState. */
export const cloneInto = (dst: SimState, src: SimState): void => {
  dst.version = src.version;
  dst.tick = src.tick;
  dst.hitStop = src.hitStop;
  dst.prng.a = src.prng.a;
  dst.prng.b = src.prng.b;
  dst.prng.c = src.prng.c;
  dst.prng.d = src.prng.d;
  copyConfig(dst.config, src.config);
  copyPlayer(dst.players[0], src.players[0]);
  copyPlayer(dst.players[1], src.players[1]);
  copyBall(dst.ball, src.ball);
  copyRules(dst.rules, src.rules);
  copyCamera(dst.camera, src.camera);
};

/** Allocate a deep copy (for snapshot ring buffers; not for the hot loop). */
export const cloneState = (src: SimState): SimState => {
  const dst = createBlankState();
  cloneInto(dst, src);
  return dst;
};
